// src/lib/webVitals.ts
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import type { Metric } from 'web-vitals';
import * as Sentry from '@sentry/react';
import { mixpanelService } from './mixpanelService';
import logger from './logger';

/**
 * Map web-vitals metric names to human-readable labels
 */
const METRIC_LABELS: Record<string, string> = {
  CLS: 'Cumulative Layout Shift',
  FCP: 'First Contentful Paint',
  INP: 'Interaction to Next Paint',
  LCP: 'Largest Contentful Paint',
  TTFB: 'Time to First Byte',
};

/**
 * Report a single web vital metric to Sentry and Mixpanel
 */
function reportMetric(metric: Metric): void {
  const { name, value, rating, id } = metric;

  logger.log(`Web Vital [${name}]: ${value} (${rating})`);

  // Report to Sentry as a custom measurement on the active span
  const activeSpan = Sentry.getActiveSpan();
  const rootSpan = activeSpan ? Sentry.getRootSpan(activeSpan) : undefined;
  if (rootSpan) {
    // Attach web vital as a measurement on the current transaction
    // CLS is unitless, all others are milliseconds
    const unit = name === 'CLS' ? '' : 'millisecond';
    rootSpan.setAttribute(`web_vital.${name.toLowerCase()}`, value);
    rootSpan.setAttribute(`web_vital.${name.toLowerCase()}.rating`, rating);

    Sentry.setMeasurement(name, value, unit);
  }

  // Report to Mixpanel for product analytics correlation
  mixpanelService.track('Web Vital Measured', {
    metric_name: name,
    metric_label: METRIC_LABELS[name] || name,
    metric_value: Math.round(name === 'CLS' ? value * 1000 : value), // CLS scaled for readability
    metric_rating: rating,
    metric_id: id,
    page_path: window.location.pathname,
  });
}

/**
 * Initialize web vitals measurement and reporting.
 * Measures CLS, FCP, INP, LCP, and TTFB, reporting each to
 * Sentry (as custom measurements) and Mixpanel (as analytics events).
 *
 * Call this after Sentry and Mixpanel have been initialized.
 */
export function initWebVitals(): void {
  try {
    onCLS(reportMetric);
    onFCP(reportMetric);
    onINP(reportMetric);
    onLCP(reportMetric);
    onTTFB(reportMetric);

    logger.log('Web Vitals instrumentation initialized');
  } catch (error) {
    logger.error('Failed to initialize Web Vitals:', error);
  }
}
